/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createAxiosInstance } from '../utils/Axios';
import { toast } from 'react-toastify';
import { useAuth, hasModuleAccess, hasSectionAccess } from '../utils/hooks/useAuth';

export default function CrmCampaigns() {
  const { access, loading: authLoading } = useAuth();

  // Check module and section permissions
  const hasCrmAccess = access ? hasModuleAccess('iurm', access) : false;
  const canViewCampaigns = access ? hasSectionAccess('iurm', 'Campaigns', 'view', access) : false;
  const canCreateCampaign = access ? hasSectionAccess('iurm', 'Campaigns', 'create', access) : false;
  const canEditCampaign = access ? hasSectionAccess('iurm', 'Campaigns', 'edit', access) : false;
  const canDeleteCampaign = access ? hasSectionAccess('iurm', 'Campaigns', 'delete', access) : false;

  // Check other CRM section permissions for tabs
  const canViewOverview = access ? hasSectionAccess('iurm', 'Overview', 'view', access) : false;
  const canViewLeads = access ? hasSectionAccess('iurm', 'Leads', 'view', access) : false;
  const canViewContacts = access ? hasSectionAccess('iurm', 'Contacts', 'view', access) : false;
  const canViewAccounts = access ? hasSectionAccess('iurm', 'Accounts', 'view', access) : false;
  const canViewTasks = access ? hasSectionAccess('iurm', 'Tasks', 'view', access) : false;
  const canViewIntegration = access ? hasSectionAccess('iurm', 'Integration', 'view', access) : false;

  // eslint-disable-next-line no-unused-vars
  const [activeTab, setActiveTab] = useState('All Campaigns');
  const [viewMode, setViewMode] = useState('table');
  
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedEcosystem, setSelectedEcosystem] = useState('');
  const [ecosystems, setEcosystems] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [stats, setStats] = useState({
    totalCampaigns: 0,
    activeCampaigns: 0,
    completedCampaigns: 0
  });

  // Modal States
  const [showAddCampaignModal, setShowAddCampaignModal] = useState(false);
  const [newCampaign, setNewCampaign] = useState({ name: '', category: '', status: 'Open', amount: '', paid: '', progress: 0, startDate: '', endDate: '' });
  const [isSavingCampaign, setIsSavingCampaign] = useState(false);

  const [showEditCampaignModal, setShowEditCampaignModal] = useState(false);
  const [editCampaignData, setEditCampaignData] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [showDeleteCampaignModal, setShowDeleteCampaignModal] = useState(false);
  const [deleteCampaignId, setDeleteCampaignId] = useState(null);
  const [isDeletingCampaign, setIsDeletingCampaign] = useState(false);

  const [openDropdownId, setOpenDropdownId] = useState(null);

  useEffect(() => {
    console.log('CRM Campaigns Permissions:', {
      hasCrmAccess,
      canViewCampaigns,
      canCreateCampaign,
      canEditCampaign,
      canDeleteCampaign
    });
  }, [hasCrmAccess, canViewCampaigns, canCreateCampaign, canEditCampaign, canDeleteCampaign]);

  useEffect(() => {
    const fetchCampaigns = async () => {
      setIsLoading(true);
      const mockCampaigns = [
        { _id: 'c1', title: 'Offer sale', description: 'Offer sale', status: 'Open', paidUsers: 0, totalMembers: 1, amountPayablePerUser: 0, progress: 0, createdAt: '2025-06-03', endDate: '2025-07-01' },
        { _id: 'c2', title: 'iXES 30%', description: 'iXES 30%', status: 'Open', paidUsers: 1, totalMembers: 4, amountPayablePerUser: 0, progress: 0, createdAt: '2025-06-17', endDate: '2025-06-30' },
        { _id: 'c3', title: 'NSDI', description: 'NSDI', status: 'Open', paidUsers: 0, totalMembers: 6, amountPayablePerUser: 0, progress: 0, createdAt: '2026-02-25', endDate: '2026-02-25' },
        { _id: 'c4', title: 'Education', description: 'Education', status: 'Open', paidUsers: 0, totalMembers: 9, amountPayablePerUser: 1000, progress: 0, createdAt: '2026-04-21', endDate: '2026-04-22' },
        { _id: 'c5', title: 'Community Growth Fund', description: 'Community Growth Fund', status: 'Open', paidUsers: 1, totalMembers: 1, amountPayablePerUser: 10, progress: 100, createdAt: '2025-12-16', endDate: '2025-12-31' },
      ];

      try {
        const axiosInstance = createAxiosInstance();
        const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
        
        let queryParams = `?timestamp=${new Date().toISOString()}`;
        if (searchTerm) queryParams += `&search=${searchTerm}`;
        if (selectedEcosystem) queryParams += `&community=${selectedEcosystem}`;

        const { data } = await axiosInstance.get(`${apiUrl}/campaigns${queryParams}`);
        let campaignsData = mockCampaigns;
        if (Array.isArray(data) && data.length > 0) {
          campaignsData = data;
        }

        const mappedCampaigns = campaignsData.map(c => ({
          ...c,
          name: c.title,
          category: c.description || 'General',
          amount: c.amountPayablePerUser || 0,
          paid: `${c.paidUsers || 0} / ${c.totalMembers || 0} Paid`,
          progress: c.progress || 0,
          initials: c.title ? c.title.substring(0, 2).toUpperCase() : 'CP',
          startDate: c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '-',
          endDate: c.endDate ? new Date(c.endDate).toLocaleDateString() : '-',
        }));
        setCampaigns(mappedCampaigns);
        setStats({
          totalCampaigns: campaignsData.length,
          activeCampaigns: campaignsData.filter(c => !c.isCompleted).length,
          completedCampaigns: campaignsData.filter(c => c.isCompleted).length
        });
      } catch (error) {
        console.error("Failed to fetch campaigns", error);
        const mappedCampaigns = mockCampaigns.map(c => ({
          ...c,
          name: c.title,
          category: c.description || 'General',
          amount: c.amountPayablePerUser || 0,
          paid: `${c.paidUsers || 0} / ${c.totalMembers || 0} Paid`,
          progress: c.progress || 0,
          initials: c.title ? c.title.substring(0, 2).toUpperCase() : 'CP',
          startDate: c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '-',
          endDate: c.endDate ? new Date(c.endDate).toLocaleDateString() : '-',
        }));
        setCampaigns(mappedCampaigns);
        setStats({
          totalCampaigns: mockCampaigns.length,
          activeCampaigns: mockCampaigns.filter(c => !c.isCompleted).length,
          completedCampaigns: mockCampaigns.filter(c => c.isCompleted).length
        });
        // toast.error("Failed to load campaigns, using mock data");
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchCampaigns();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, refreshTrigger, selectedEcosystem]);

  useEffect(() => {
    const fetchEcosystems = async () => {
      const mockEcosystems = [
        { _id: 'e1', name: 'iXES' },
        { _id: 'e2', name: 'iXES-Academy' },
        { _id: 'e3', name: 'iXES-Ops' }
      ];
      try {
        const axiosInstance = createAxiosInstance();
        const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
        const { data } = await axiosInstance.get(`${apiUrl}/communities/list`);
        if (data && data.data && data.data.length > 0) {
          setEcosystems(data.data);
        } else if (Array.isArray(data) && data.length > 0) {
          setEcosystems(data);
        } else {
          setEcosystems(mockEcosystems);
        }
      } catch (error) {
        console.error("Failed to fetch ecosystems", error);
        setEcosystems(mockEcosystems);
      }
    };
    fetchEcosystems();
  }, []);

  const handleSaveCampaign = async () => {
    if (!newCampaign.name) {
      toast.error("Campaign Name is required");
      return;
    }
    setIsSavingCampaign(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      
      const campaignPayload = { 
        title: newCampaign.name, 
        description: newCampaign.category || 'General', 
        status: newCampaign.status, 
        amountPerUser: newCampaign.amount || 0, 
        totalMembers: 0,
        progress: newCampaign.progress || 0, 
        campaignType: 'PAID',
        communityId: null,
        endDate: newCampaign.endDate ? new Date(newCampaign.endDate).toISOString() : null
      };

      await axiosInstance.post(`${apiUrl}/campaigns/create`, campaignPayload);
      
      toast.success("Campaign created successfully!");
      setShowAddCampaignModal(false);
      setNewCampaign({ name: '', category: '', status: 'Open', amount: '', paid: '', progress: 0, startDate: '', endDate: '' });
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Save failed", error);
      toast.error(error?.response?.data?.message || "Failed to create campaign");
    } finally {
      setIsSavingCampaign(false);
    }
  };

  const handleUpdateCampaign = async () => {
    if (!editCampaignData.name) {
      toast.error("Campaign Name is required");
      return;
    }
    setIsSavingEdit(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      
      const campaignPayload = { 
        title: editCampaignData.name || editCampaignData.title, 
        description: editCampaignData.category || editCampaignData.description, 
        community: [] // Required by backend API mapping
      };

      await axiosInstance.put(`${apiUrl}/campaigns/${editCampaignData._id}`, campaignPayload);
      
      toast.success("Campaign updated successfully!");
      setShowEditCampaignModal(false);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Update failed", error);
      toast.error(error?.response?.data?.message || "Failed to update campaign");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!deleteCampaignId) return;
    setIsDeletingCampaign(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      
      await axiosInstance.post(`${apiUrl}/campaigns/delete`, { campaignId: deleteCampaignId });
      
      toast.success("Campaign deleted successfully!");
      setShowDeleteCampaignModal(false);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Delete failed", error);
      toast.error(error?.response?.data?.message || "Failed to delete campaign");
    } finally {
      setIsDeletingCampaign(false);
      setDeleteCampaignId(null);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Open': return { bg: '#D1FAE5', color: '#059669' };
      case 'Completed': return { bg: '#D1FAE5', color: '#059669' };
      case 'Upcoming': return { bg: '#DBEAFE', color: '#2563EB' };
      case 'Closed': return { bg: '#F3F4F6', color: '#4B5563' };
      default: return { bg: '#F3F4F6', color: '#4B5563' };
    }
  };

  // Helper colors for campaigns if not provided by backend
  const getProgressColor = (idx) => {
    const colors = ['#2563EB', '#7C3AED', '#5B21B6', '#F59E0B', '#0D9488', '#16A34A', '#DC2626'];
    return colors[idx % colors.length];
  };

  const getBgColor = (idx) => {
    const colors = ['#EFF6FF', '#F3E8FF', '#FFFBEB', '#FDF4FF', '#ECFEFF', '#FDF2F8', '#F0FDF4'];
    return colors[idx % colors.length];
  };

  // Show loading while checking permissions
  if (authLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
        Loading permissions...
      </div>
    );
  }

  // Show access denied if no module access or no campaigns view access
  if (!hasCrmAccess || !canViewCampaigns) {
    return (
      <div style={{ 
        padding: '60px 40px', 
        textAlign: 'center',
        backgroundColor: '#fff',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        margin: '20px'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
        <h2 style={{ color: '#111827', marginBottom: '12px' }}>Access Denied</h2>
        <p style={{ color: '#6B7280', fontSize: '16px', marginBottom: '8px' }}>
          You don't have permission to access the Campaigns section.
        </p>
        <p style={{ color: '#9CA3AF', fontSize: '14px' }}>
          Please contact your administrator to request access.
        </p>
      </div>
    );
  }

  return (
    <div className="crm-campaigns-container" style={{ padding: '16px', backgroundColor: '#f8f9fa', minHeight: '100%', fontFamily: 'Inter, sans-serif' }}>
      {/* Sub-Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #E5E7EB', marginBottom: '20px', paddingBottom: '0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          {canViewOverview && (
            <Link to="/crm/overview" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>
                Overview
              </div>
            </Link>
          )}
          {canViewLeads && (
            <Link to="/crm/leads" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                Leads
              </div>
            </Link>
          )}
          {canViewContacts && (
            <Link to="/crm/contacts" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                Contacts
              </div>
            </Link>
          )}
          {canViewAccounts && (
            <Link to="/crm/accounts" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path><path d="M8 6h.01"></path><path d="M16 6h.01"></path><path d="M12 6h.01"></path><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path></svg>
                Accounts
              </div>
            </Link>
          )}
          {canViewTasks && (
            <Link to="/crm/tasks" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>
                Tasks
              </div>
            </Link>
          )}
          <div className="nav-item active" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '999px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginBottom: '8px', backgroundColor: '#8B5CF6', color: '#fff' }}>
            <div className="nav-dot" style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'currentColor' }}></div>
            Campaigns
          </div>
          {canViewIntegration && (
            <Link to="/crm/integration" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 16V7a2 2 0 0 0-2-2h-4"></path><path d="M4 16V7a2 2 0 0 1 2-2h4"></path><rect x="9" y="15" width="6" height="6" rx="1"></rect><line x1="12" y1="5" x2="12" y2="15"></line></svg>
                MCP Integration
              </div>
            </Link>
          )}
        </div>
      </div>

      
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '28px', fontWeight: 'bold', margin: '0', color: '#111827', letterSpacing: '-0.5px' }}>Campaigns</h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button style={{ padding: '8px 16px', backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', color: '#374151', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>Export</button>
          {canCreateCampaign && (
            <button onClick={() => setShowAddCampaignModal(true)} style={{ padding: '8px 20px', backgroundColor: '#5B21B6', border: 'none', borderRadius: '8px', fontWeight: '600', color: '#fff', cursor: 'pointer', fontSize: '14px' }}>+ New Campaign</button>
          )}
        </div>
      </div>
      {isLoading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>Loading Campaigns...</div>
      ) : (
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflowX: 'auto' }}>
          <table className="table table-hover align-middle border-0 mb-0 w-100" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', backgroundColor: '#fff' }}>
            <thead>
              <tr style={{ color: '#a1a5b7', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #f1f1f4' }}>
                <th style={{ width: '5%', padding: '12px 16px', textAlign: 'left' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input type="checkbox" style={{ width: '16px', height: '16px', borderRadius: '4px', border: '1px solid #d8d8ea', cursor: 'pointer' }} />
                  </div>
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', whiteSpace: 'nowrap' }}>CAMPAIGN NAME</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', whiteSpace: 'nowrap' }}>STATUS</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', whiteSpace: 'nowrap' }}>PAID / GOAL</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', whiteSpace: 'nowrap' }}>AMOUNT</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', whiteSpace: 'nowrap' }}>PROGRESS</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', whiteSpace: 'nowrap' }}>START DATE</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', whiteSpace: 'nowrap' }}>END DATE</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', whiteSpace: 'nowrap' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody style={{ borderTop: 'none' }}>
              {campaigns.length > 0 ? campaigns.map((camp, idx) => {
                const getStatusStyle = (status) => {
                   const s = (status || '').toUpperCase();
                   if (s === 'OPEN' || s === 'ACTIVE' || s === 'COMPLETED') return { bg: '#e8fff3', text: '#50cd89' };
                   if (s === 'UPCOMING') return { bg: '#f1faff', text: '#009ef7' };
                   return { bg: '#f5f8fa', text: '#7e8299' };
                };
                const statusStyle = getStatusStyle(camp.status);
                
                return (
                <tr key={camp._id || idx} style={{ borderBottom: '1px solid #f1f1f4', transition: 'background-color 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <td style={{ padding: '12px 16px', textAlign: 'left' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <input type="checkbox" style={{ width: '16px', height: '16px', borderRadius: '4px', border: '1px solid #d8d8ea', cursor: 'pointer' }} />
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'left' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left', justifyContent: 'center' }}>
                        <div style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                          <span style={{ maxWidth: '240px', fontWeight: 'bold', color: '#181C32', textDecoration: 'none' }} onMouseOver={(e) => e.currentTarget.style.color = '#009ef7'} onMouseOut={(e) => e.currentTarget.style.color = '#181C32'}>
                            {camp.name}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'left' }}>
                    <span 
                      style={{ 
                        borderRadius: '6px', 
                        fontSize: '0.75rem',
                        backgroundColor: statusStyle.bg,
                        color: statusStyle.text,
                        fontWeight: 'bold',
                        padding: '4px 8px',
                        display: 'inline-flex',
                        alignItems: 'center'
                      }}
                    >
                      {(camp.status || '-').toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'left' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#181C32', whiteSpace: 'nowrap' }}>
                      {camp.paid}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'left' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#181C32', whiteSpace: 'nowrap' }}>
                      ₹{camp.amount ? Number(camp.amount).toFixed(2) : "0.00"}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'left' }}>
                    <div style={{ position: 'relative', width: '36px', height: '36px' }}>
                      <svg width="36" height="36" style={{ transform: 'rotate(-90deg)', overflow: 'visible' }}>
                        <circle stroke="#e1f0ff" strokeWidth="2.5" fill="transparent" r="16" cx="18" cy="18" />
                        <circle
                          stroke="#009ef7"
                          strokeWidth="2.5"
                          fill="transparent"
                          r="16"
                          cx="18"
                          cy="18"
                          strokeDasharray="100.53"
                          strokeDashoffset={100.53 - (Math.max(0, Math.min(100, camp.progress || 0)) / 100) * 100.53}
                          strokeLinecap="round"
                          style={{ transition: 'stroke-dashoffset 0.5s ease 0s' }}
                        />
                      </svg>
                      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#009ef7', lineHeight: '1' }}>{camp.progress || 0}%</span>
                        <div style={{ width: '10px', height: '1.5px', backgroundColor: '#009ef7', marginTop: '1px', borderRadius: '1px' }}></div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'left' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#181C32', whiteSpace: 'nowrap' }}>
                      {camp.startDate}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'left' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#181C32', whiteSpace: 'nowrap' }}>
                      {camp.endDate}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'left' }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <button 
                        style={{ width: '28px', height: '28px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f8fa', border: '1px solid #e9ecef', borderRadius: '6px', color: '#7e8299', cursor: 'pointer', transition: 'all 0.2s' }} 
                        onMouseOver={(e) => { e.currentTarget.style.color = '#009ef7'; e.currentTarget.style.backgroundColor = '#f1faff'; }}
                        onMouseOut={(e) => { e.currentTarget.style.color = '#7e8299'; e.currentTarget.style.backgroundColor = '#f5f8fa'; }}
                        title="View folder"
                      >
                        <svg width="12" height="12" fill="currentColor" viewBox="0 0 16 16"><path d="M9.828 3h3.982a2 2 0 0 1 1.992 2.181l-.637 7A2 2 0 0 1 13.174 14H2.825a2 2 0 0 1-1.991-1.819l-.637-7a1.99 1.99 0 0 1 .342-1.31L.5 3a2 2 0 0 1 2-2h3.672a2 2 0 0 1 1.414.586l.828.828A2 2 0 0 0 9.828 3zm-8.322.12C1.72 3.042 1.95 3 2.19 3h5.396l-.707-.707A1 1 0 0 0 6.172 2H2.5a1 1 0 0 0-1 .981l.006.139z"/></svg>
                      </button>
                      <button 
                        style={{ width: '28px', height: '28px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f8fa', border: '1px solid #e9ecef', borderRadius: '6px', color: '#7e8299', cursor: 'pointer', transition: 'all 0.2s' }} 
                        onMouseOver={(e) => { e.currentTarget.style.color = '#009ef7'; e.currentTarget.style.backgroundColor = '#f1faff'; }}
                        onMouseOut={(e) => { e.currentTarget.style.color = '#7e8299'; e.currentTarget.style.backgroundColor = '#f5f8fa'; }}
                        title="View"
                      >
                        <svg width="12" height="12" fill="currentColor" viewBox="0 0 16 16"><path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/><path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/></svg>
                      </button>
                      {canEditCampaign && (
                        <button 
                          onClick={() => { setEditCampaignData(camp); setShowEditCampaignModal(true); }}
                          style={{ width: '28px', height: '28px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f8fa', border: '1px solid #e9ecef', borderRadius: '6px', color: '#7e8299', cursor: 'pointer', transition: 'all 0.2s' }} 
                          onMouseOver={(e) => { e.currentTarget.style.color = '#009ef7'; e.currentTarget.style.backgroundColor = '#f1faff'; }}
                          onMouseOut={(e) => { e.currentTarget.style.color = '#7e8299'; e.currentTarget.style.backgroundColor = '#f5f8fa'; }}
                          title="Edit"
                        >
                          <svg width="10" height="10" fill="currentColor" viewBox="0 0 16 16"><path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/></svg>
                        </button>
                      )}
                      {canDeleteCampaign && (
                        <div style={{ position: 'relative' }}>
                          <button 
                            onClick={() => { setOpenDropdownId(openDropdownId === camp._id ? null : camp._id); }}
                            style={{ width: '28px', height: '28px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f8fa', border: '1px solid #e9ecef', borderRadius: '6px', color: '#7e8299', cursor: 'pointer', transition: 'all 0.2s' }} 
                            onMouseOver={(e) => { e.currentTarget.style.color = '#009ef7'; e.currentTarget.style.backgroundColor = '#f1faff'; }}
                            onMouseOut={(e) => { e.currentTarget.style.color = '#7e8299'; e.currentTarget.style.backgroundColor = '#f5f8fa'; }}
                            title="More Actions"
                          >
                            <svg width="12" height="12" fill="currentColor" viewBox="0 0 16 16"><path d="M3 9.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z"/></svg>
                          </button>
                          {openDropdownId === camp._id && (
                            <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: '4px', backgroundColor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: '8px', zIndex: 10, minWidth: '150px', padding: '8px 0', border: '1px solid #e9ecef' }}>
                              <button 
                                onClick={() => { setDeleteCampaignId(camp._id); setShowDeleteCampaignModal(true); setOpenDropdownId(null); }}
                                style={{ width: '100%', padding: '8px 16px', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer', color: '#dc3545', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px' }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <svg width="12" height="12" fill="currentColor" viewBox="0 0 16 16"><path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/><path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/></svg>
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
                );
              }) : (
                <tr><td colSpan="9" style={{ padding: '24px', textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>No campaigns found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {showAddCampaignModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '12px', width: '400px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Add Campaign</h3>
            <input placeholder="Name" value={newCampaign.name} onChange={e => setNewCampaign({...newCampaign, name: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '12px', border: '1px solid #D1D5DB', borderRadius: '6px' }} />
            <input placeholder="Type" value={newCampaign.type} onChange={e => setNewCampaign({...newCampaign, type: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '12px', border: '1px solid #D1D5DB', borderRadius: '6px' }} />
            <input placeholder="Status" value={newCampaign.status} onChange={e => setNewCampaign({...newCampaign, status: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '12px', border: '1px solid #D1D5DB', borderRadius: '6px' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setShowAddCampaignModal(false)} style={{ padding: '8px 16px', background: '#fff', border: '1px solid #D1D5DB', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSaveCampaign} disabled={isSavingCampaign} style={{ padding: '8px 16px', background: '#5B21B6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}

</div>);
}